import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { cx } from '../ui/classNames';
import { escapeAttributeValue } from './dom';
import { alignClassNames } from './tableClasses';

// Keys that start an edit with the typed character as the input's text.
const SEED_KEY = /^[\d.-]$/;

/**
 * Reads typed text as a number: commas are ignored ('1,234.5'), a leading
 * minus and a bare decimal point work ('-12', '.5'), and empty text is
 * null, as is a number not yet started ('-', '.', '-.'), as the position
 * sizing editors read them. Anything else is invalid.
 */
export function parseNumberInput(text) {
    const normalized = String(text).replace(/,/g, '').trim();
    if (/^-?\.?$/.test(normalized)) return { valid: true, value: null };
    if (!/^-?(\d+\.?\d*|\.\d+)$/.test(normalized)) return { valid: false, value: null };
    return { valid: true, value: Number(normalized) };
}

// The editor's starting text: the formatted value when it reads back as a
// number, else the raw value.
function editText(value, format) {
    if (value == null || value === '') return '';
    const formatted = format(value);
    return parseNumberInput(formatted).valid ? formatted : String(value);
}

// The focusable content of the same column's cell `step` data rows away
// (DataTable rows carry data-row-id, cells data-field); null at either end
// or when that cell has nothing to focus.
function neighbourCellTarget(element, step) {
    const cell = element.closest('td[data-field]');
    const field = cell?.getAttribute('data-field');
    if (!field) return null;
    let row = cell.parentElement;
    do {
        row = step > 0 ? row.nextElementSibling : row.previousElementSibling;
    } while (row && !row.hasAttribute('data-row-id'));
    return row?.querySelector(`td[data-field="${escapeAttributeValue(field)}"] [tabindex="0"]`) || null;
}

const DISPLAY_CLASS_NAME ='block w-full min-w-0 cursor-text truncate rounded-sm focus:outline-none '
    + 'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ui-focus';
const INPUT_CLASS_NAME = 'm-0 block h-7 w-full min-w-0 appearance-none rounded-md border border-solid bg-ui-surface '
    + 'px-1.5 py-0 text-[length:inherit] font-semibold leading-[inherit] text-content-primary [font-family:inherit] '
    + 'focus:outline-none [[data-compact]_&]:h-6';
const INPUT_STATE_CLASS_NAMES = Object.freeze({
    valid: 'border-hairline focus:border-accent focus:shadow-[0_0_0_2px_rgb(var(--c-accent)/0.16)]',
    invalid: 'border-negative shadow-[0_0_0_2px_rgb(var(--c-negative)/0.16)]',
});

/**
 * A number cell that edits in place, for a DataTable column's `cell`.
 *
 * Shows `format(value)`. When `editable`, the text is focusable and an edit
 * starts on click, Enter, Space, F2, or typing a digit, '-' or '.' (that
 * character becomes the input's text). The input (inputMode decimal) takes
 * focus with its text selected, or the caret after the typed character.
 * Typed text is read by parseNumberInput; text that is not a number shows
 * an error and is not committed.
 *
 * Enter commits and, like a spreadsheet, moves focus to the same column's
 * cell in the next row (Shift+Enter: the previous row), else back to this
 * cell; Tab commits and lets focus move on; leaving the input commits;
 * Escape cancels. A commit calls
 * `onCommit(nextValue)` only when the number changed (and the text was
 * edited). If it returns a promise the cell shows the new value until it
 * settles, and the previous value again if it rejects. `onEditingChange`
 * reports when editing starts and stops. Keys typed in the input never reach
 * the row, so a clickable row cannot activate from the editor.
 *
 * `formatInput` can preserve extra precision while `format` rounds the display.
 * `aria-label` names the input and, with the value, the cell.
 */
export default function EditableNumberCell({
    value,
    format,
    formatInput = format,
    editable = true,
    onCommit,
    onEditingChange,
    align = 'right',
    'aria-label': ariaLabel,
    className,
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [invalid, setInvalid] = useState(false);
    // The committed value while onCommit's promise is pending.
    const [pending, setPending] = useState(null);
    const startTextRef = useRef('');
    const caretRef = useRef('select');
    const closingRef = useRef(false);
    const refocusRef = useRef(false);
    const commitCountRef = useRef(0);
    const endEditOnUnmountRef = useRef(null);

    // Stable, so React calls them only when their element mounts or unmounts.
    const focusInput = useCallback((input) => {
        if (!input) {
            // Unmounted mid-edit (a virtualized row scrolled away): end the
            // edit as leaving the input would.
            if (!closingRef.current) endEditOnUnmountRef.current?.();
            return;
        }
        input.focus();
        if (caretRef.current === 'end') input.setSelectionRange(input.value.length, input.value.length);
        else input.select();
    }, []);
    const refocusDisplay = useCallback((element) => {
        if (!element || !refocusRef.current) return;
        refocusRef.current = false;
        element.focus();
    }, []);

    const text = format(pending ? pending.value : value) ?? '';
    const alignClassName = alignClassNames[align];
    if (!editable) {
        return <span className={cx('block min-w-0 truncate', alignClassName, className)}>{text}</span>;
    }

    const startEditing = (seed) => {
        const initial = seed ?? editText(value, formatInput);
        startTextRef.current = seed == null ? initial : null;
        caretRef.current = seed == null ? 'select' : 'end';
        closingRef.current = false;
        setDraft(initial);
        setInvalid(false);
        setEditing(true);
        onEditingChange?.(true);
    };
    const stopEditing = (refocus) => {
        closingRef.current = true;
        refocusRef.current = refocus;
        setEditing(false);
        setInvalid(false);
        onEditingChange?.(false);
    };
    // False when the text is not a number; the editor then stays open.
    const commit = (refocus) => {
        if (draft === startTextRef.current) {
            stopEditing(refocus);
            return true;
        }
        const parsed = parseNumberInput(draft);
        if (!parsed.valid) {
            setInvalid(true);
            return false;
        }
        stopEditing(refocus);
        const previous = value == null || value === '' ? null : Number(value);
        if (parsed.value === previous) return true;
        commitCountRef.current += 1;
        const commitNumber = commitCountRef.current;
        const result = onCommit?.(parsed.value);
        if (result && typeof result.then === 'function') {
            setPending({ value: parsed.value });
            // Only the latest commit clears the pending value.
            const settle = () => {
                if (commitCountRef.current === commitNumber) setPending(null);
            };
            result.then(settle, settle);
        }
        return true;
    };
    endEditOnUnmountRef.current = () => {
        if (!commit(false)) stopEditing(false);
    };

    if (!editing) {
        return (
            <div
                ref={refocusDisplay}
                role="button"
                tabIndex={0}
                aria-label={ariaLabel && (text ? `${ariaLabel}: ${text}` : ariaLabel)}
                aria-busy={pending ? true : undefined}
                className={cx(DISPLAY_CLASS_NAME, alignClassName, className)}
                onClick={() => startEditing()}
                onKeyDown={(event) => {
                    if (event.ctrlKey || event.metaKey || event.altKey) return;
                    let seed;
                    if (SEED_KEY.test(event.key)) seed = event.key;
                    else if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'F2') return;
                    event.preventDefault();
                    event.stopPropagation();
                    startEditing(seed);
                }}
            >
                {text}
            </div>
        );
    }

    return (
        <input
            ref={focusInput}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            aria-label={ariaLabel}
            aria-invalid={invalid || undefined}
            value={draft}
            className={cx(
                INPUT_CLASS_NAME,
                invalid ? INPUT_STATE_CLASS_NAMES.invalid : INPUT_STATE_CLASS_NAMES.valid,
                alignClassName,
                className,
            )}
            onChange={(event) => {
                setDraft(event.target.value);
                if (invalid) setInvalid(false);
            }}
            onKeyDown={(event) => {
                event.stopPropagation();
                if (event.key === 'Enter') {
                    event.preventDefault();
                    const next = neighbourCellTarget(event.currentTarget, event.shiftKey ? -1 : 1);
                    if (commit(!next) && next) next.focus();
                } else if (event.key === 'Escape') {
                    event.preventDefault();
                    stopEditing(true);
                } else if (event.key === 'Tab' && draft !== startTextRef.current && !parseNumberInput(draft).valid) {
                    // Valid text commits on the blur that Tab causes.
                    event.preventDefault();
                    setInvalid(true);
                }
            }}
            onClick={(event) => event.stopPropagation()}
            onBlur={() => {
                if (!closingRef.current) commit(false);
            }}
        />
    );
}

EditableNumberCell.propTypes = {
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    format: PropTypes.func.isRequired,
    formatInput: PropTypes.func,
    editable: PropTypes.bool,
    onCommit: PropTypes.func,
    onEditingChange: PropTypes.func,
    align: PropTypes.oneOf(['left', 'center', 'right']),
    'aria-label': PropTypes.string,
    className: PropTypes.string,
};
